import { HttpClient } from '@angular/common/http';
import { Inject, Injectable } from '@angular/core';
import type { LatLngBounds } from 'leaflet';
import { latLngBounds } from 'leaflet';
import type { Observable } from 'rxjs';
import { of } from 'rxjs';
import { catchError, map, shareReplay } from 'rxjs/operators';
import { environment } from 'src/environments/environment';

/** Shape of the /core/organizations/area response. */
interface OrganizationAreaResponse {
  extent: {
    min_longitude: number;
    min_latitude: number;
    max_longitude: number;
    max_latitude: number;
  } | null;
}

/**
 * Provides the organization's operating area — the bounding box of the organization's (permitted)
 * vehicles' latest known locations — used to frame maps on first load.
 */
@Injectable({ providedIn: 'root' })
export class MapOrganizationAreaService {

  /** API url. */
  private readonly apiUrl = `${environment.apiBaseUrl}/core/organizations/area`;

  /** Session-cached request so every map shares a single fetch. */
  private orgArea$?: Observable<LatLngBounds | null>;

  public constructor(@Inject(HttpClient) private readonly http: HttpClient) { }

  /**
   * Gets the organization's area as Leaflet bounds, cached for the session.
   * Emits null when there is nothing to fit to (no located vehicles) or the caller lacks permission
   * (403) — consumers fall back to their default map view.
   * @returns Observable of LatLngBounds, or null when there is no area to fit to.
   */
  public getOrgArea(): Observable<LatLngBounds | null> {
    if (!this.orgArea$) {
      this.orgArea$ = this.http.get<OrganizationAreaResponse>(this.apiUrl).pipe(
        map(response => {
          if (!response.extent) {
            return null;
          }

          return latLngBounds(
            [response.extent.min_latitude, response.extent.min_longitude],
            [response.extent.max_latitude, response.extent.max_longitude],
          );
        }),
        catchError(() => of(null)),
        shareReplay(1),
      );
    }

    return this.orgArea$;
  }

}
